'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertCircle, ChevronRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface ClientRegisterData {
  fullName: string;
  email: string;
  mobile: string;
  country: string;
  preferredLanguage: string;
  gender: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const countries = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Singapore', 'UAE', 'Other'];
const languages = ['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Marathi', 'Gujarati'];

interface ClientRegisterFormProps {
  onSwitchToLogin: () => void;
}

export default function ClientRegisterForm({ onSwitchToLogin }: ClientRegisterFormProps) {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signUp } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<ClientRegisterData>();

  const password = watch('password');

  const handleNext = async () => {
    const valid = await trigger(['fullName', 'email', 'mobile', 'country', 'preferredLanguage']);
    if (valid) setStep(2);
  };

  const onSubmit = async (data: ClientRegisterData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        role: 'client',
        phone: data.mobile,
        country: data.country,
        preferredLanguage: data.preferredLanguage,
        gender: data.gender,
      });
      toast.success('Account created! Welcome to Healing52.');
      router.push('/client-dashboard');
    } catch (err: any) {
      toast.error(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    { num: 1, label: 'Personal Info' },
    { num: 2, label: 'Security' },
  ];

  return (
    <div className="fade-in">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">Create Client Account</h2>
        <p className="text-sm text-muted-foreground">Begin your healing journey today</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2 mb-7">
        {steps.map((s, idx) => (
          <React.Fragment key={`client-step-${s.num}`}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step > s.num ? 'bg-success text-white' : step === s.num ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > s.num ? <CheckCircle2 size={14} /> : s.num}
              </div>
              <span className={`text-xs font-medium ${step === s.num ? 'text-primary' : 'text-muted-foreground'}`}>
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-px ${step > s.num ? 'bg-success' : 'bg-border'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1 */}
        {step === 1 && (
          <div className="flex flex-col gap-4 slide-up">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="c-fullName">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                id="c-fullName"
                type="text"
                placeholder="e.g. Priya Sharma"
                className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.fullName ? 'border-danger' : 'border-border'}`}
                {...register('fullName', { required: 'Full name is required' })}
              />
              {errors.fullName && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.fullName.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="c-email">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                id="c-email"
                type="email"
                placeholder="you@example.com"
                className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.email ? 'border-danger' : 'border-border'}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="c-mobile">
                Mobile Number <span className="text-danger">*</span>
              </label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 bg-muted border border-border rounded-lg text-sm text-muted-foreground font-medium">+91</div>
                <input
                  id="c-mobile"
                  type="tel"
                  placeholder="9876543210"
                  className={`flex-1 px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.mobile ? 'border-danger' : 'border-border'}`}
                  {...register('mobile', {
                    required: 'Mobile number is required',
                    pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit mobile number' },
                  })}
                />
              </div>
              {errors.mobile && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.mobile.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="c-country">
                  Country <span className="text-danger">*</span>
                </label>
                <select
                  id="c-country"
                  className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all ${errors.country ? 'border-danger' : 'border-border'}`}
                  {...register('country', { required: 'Country is required' })}
                >
                  <option value="">Select</option>
                  {countries.map((c) => <option key={`country-${c}`} value={c}>{c}</option>)}
                </select>
                {errors.country && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.country.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="c-lang">
                  Language <span className="text-danger">*</span>
                </label>
                <select
                  id="c-lang"
                  className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all ${errors.preferredLanguage ? 'border-danger' : 'border-border'}`}
                  {...register('preferredLanguage', { required: 'Language is required' })}
                >
                  <option value="">Select</option>
                  {languages.map((l) => <option key={`lang-${l}`} value={l}>{l}</option>)}
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={handleNext}
              className="w-full py-3 bg-primary text-primary-foreground font-semibold text-sm rounded-lg hover:bg-primary/90 active:scale-95 transition-all flex items-center justify-center gap-2 mt-1"
            >
              Continue <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="flex flex-col gap-4 slide-up">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="c-password">
                Create Password <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">Min. 8 characters with at least one number and one letter</p>
              <div className="relative">
                <input
                  id="c-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  className={`w-full px-4 py-2.5 pr-11 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.password ? 'border-danger' : 'border-border'}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    pattern: { value: /^(?=.*[A-Za-z])(?=.*\d)/, message: 'Password must contain letters and numbers' },
                  })}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.password.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="c-confirm">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <input
                  id="c-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  className={`w-full px-4 py-2.5 pr-11 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.confirmPassword ? 'border-danger' : 'border-border'}`}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (v) => v === password || 'Passwords do not match',
                  })}
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.confirmPassword.message}</p>}
            </div>

            <div className="flex items-start gap-2.5 mt-1">
              <input
                id="c-agree"
                type="checkbox"
                className="w-4 h-4 mt-0.5 rounded border-border accent-primary"
                {...register('agreeTerms', { required: 'You must agree to the terms' })}
              />
              <label htmlFor="c-agree" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                I agree to the <span className="text-primary font-semibold">Terms of Service</span>, <span className="text-primary font-semibold">Privacy Policy</span>, and <span className="text-primary font-semibold">Healing Session Disclaimer</span>
              </label>
            </div>
            {errors.agreeTerms && <p className="text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.agreeTerms.message}</p>}

            <div className="flex gap-3 mt-1">
              <button type="button" onClick={() => setStep(1)} className="flex-1 py-3 text-sm font-semibold text-foreground border border-border rounded-lg hover:border-primary/40 transition-all">
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-3 bg-primary text-primary-foreground font-semibold text-sm rounded-lg hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : 'Create Account'}
              </button>
            </div>
          </div>
        )}
      </form>

      <p className="text-center text-sm text-muted-foreground mt-5">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">
          Sign in
        </button>
      </p>
    </div>
  );
}